import { useEffect } from 'react'
import { Link } from 'react-router-dom'

export function FreedAlternativePage() {
  useEffect(() => {
    document.title = 'Freed AI Alternative — Dr. Scribey | AI Medical Scribe'
    const meta = document.querySelector('meta[name="description"]')
    if (meta) meta.setAttribute('content', 'Looking for a Freed AI alternative? Dr. Scribey is an agentic AI medical scribe that learns your style, remembers your patients, and transfers notes to your EHR in one click. Try free.')
  }, [])

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div className="bg-gradient-to-br from-indigo-50 to-white pt-16 pb-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block bg-indigo-100 text-indigo-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-6">
            Freed AI Alternative
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            A smarter Freed alternative<br className="hidden md:block" /> that actually learns your style
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Freed transcribes. Dr. Scribey thinks. It remembers your patients, learns how you write, and transfers notes to your EHR — no copy-paste.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" className="px-8 py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl text-lg transition-colors shadow-lg shadow-indigo-200">
              Try free — 10 encounters
            </Link>
            <Link to="/login" className="px-8 py-4 bg-white border-2 border-gray-200 hover:border-indigo-300 text-gray-700 font-semibold rounded-xl text-lg transition-colors">
              Sign in
            </Link>
          </div>
          <p className="text-sm text-gray-400 mt-4">No credit card. No contract. HIPAA compliant.</p>
        </div>
      </div>

      {/* Comparison table */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-10">
          Dr. Scribey vs. Freed AI
        </h2>
        <div className="overflow-x-auto rounded-2xl border border-gray-200 shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-6 py-4 font-semibold text-gray-500 w-1/2">Feature</th>
                <th className="text-center px-6 py-4 font-bold text-indigo-700 w-1/4">Dr. Scribey</th>
                <th className="text-center px-6 py-4 font-semibold text-gray-500 w-1/4">Freed AI</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {[
                ['Learns your writing style', '✓ Adapts after every note', '✗ Template-based'],
                ['Remembers patients across visits', '✓ Patient memory graph', '✗ Each visit is fresh'],
                ['Op notes & consult notes', '✓ All note types', '✗ SOAP/clinic focus'],
                ['EHR transfer (no copy-paste)', '✓ One-click via Chrome ext', '✗ Manual copy-paste'],
                ['Practice Fusion support', '✓ Built-in', 'Limited'],
                ['"My usual but..."', '✓ Understands context', '✗ Not supported'],
                ['Price', '$99/mo', '$149/mo+'],
                ['Contract', 'None — cancel anytime', 'Annual plans'],
                ['Free trial', '10 full encounters free', 'Limited'],
              ].map(([feat, us, them], i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                  <td className="px-6 py-4 text-gray-700 font-medium">{feat}</td>
                  <td className="px-6 py-4 text-center text-indigo-700 font-semibold">{us}</td>
                  <td className="px-6 py-4 text-center text-gray-400">{them}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Why section */}
      <div className="bg-indigo-50 py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-12">
            Why doctors switch from Freed to Dr. Scribey
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: '🧠',
                title: 'It thinks, not just transcribes',
                body: 'Freed produces a decent SOAP note from a transcript. Dr. Scribey understands clinical context — it knows the difference between a new complaint and an ongoing condition, and documents accordingly.',
              },
              {
                icon: '📋',
                title: 'Every note type, not just clinic',
                body: 'Op notes. Consult letters. H&Ps. Procedure notes. Just say "op note for Mrs. Smith, right TKA, the usual except we released the ITB" — Dr. Scribey knows what that means.',
              },
              {
                icon: '🔗',
                title: 'Into your EHR in one click',
                body: 'Our Chrome extension injects the note directly into Practice Fusion, Epic, Athena, and more. No copy-paste. No tab-switching. You finish the note and it\'s in the chart.',
              },
            ].map((card, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-sm border border-indigo-100">
                <div className="text-3xl mb-4">{card.icon}</div>
                <h3 className="font-bold text-gray-900 mb-2">{card.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{card.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Social proof */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-10">What doctors say</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {[
            {
              quote: "I used Freed for 6 months. Dr. Scribey is what Freed should have been. After two weeks it sounds like me.",
              author: "Orthopedic Surgeon, CA",
            },
            {
              quote: "The Practice Fusion integration alone was worth switching. I save 15 minutes per shift just on copy-pasting.",
              author: "Family Medicine, FL",
            },
          ].map((t, i) => (
            <div key={i} className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
              <p className="text-gray-700 italic mb-4">"{t.quote}"</p>
              <p className="text-sm font-semibold text-gray-500">— {t.author}</p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="bg-indigo-600 py-16 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to try a smarter alternative?
          </h2>
          <p className="text-indigo-200 mb-8 text-lg">
            10 full encounters free. No credit card. Set up in 2 minutes.
          </p>
          <Link to="/register" className="inline-block px-10 py-4 bg-white text-indigo-700 font-bold rounded-xl text-lg hover:bg-indigo-50 transition-colors shadow-xl">
            Start free →
          </Link>
        </div>
      </div>

      {/* Footer nav */}
      <div className="bg-gray-900 py-8 px-6">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-gray-400 text-sm">© 2026 TailwindX, LLC — Dr. Scribey</p>
          <div className="flex gap-6 text-sm text-gray-400">
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/baa" className="hover:text-white">BAA</Link>
            <Link to="/security" className="hover:text-white">Security</Link>
          </div>
        </div>
      </div>
    </div>
  )
}
